// app/pages/timeLogs/timeLogs.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Flex,
  Badge,
  VStack,
  HStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons"; // Import Chakra UI icons
import {
  timeLogsContainer,
  statBox,
  statLabel,
  statValue,
  logItem,
  logTime,
  logsScrollable,
  statBoxOverdue,
  statBoxUpcoming,
} from "./timeLogsStyle.js";
import timeLogsDB from "../../utils/TimeLogsDB";
import { useRoot } from "../../context/RootContext.jsx";
import { useMediaQuery } from "react-responsive";
import { getTaskType } from "../../utils/accordion/accordionHelper.js";
import { isNotEmptyOrNull } from "../../utils/common-utils.js";
import pkg from "@inrscr/coschool-ui-components";

const INITIAL_VISIBLE = 3;
const INITIAL_VISIBLE_MOBILE = 2;

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getDate()} ${date.toLocaleString("default", {
    month: "short",
  })}`;
};

// Helper function to convert seconds to minutes
const secondsToMinutes = (seconds) => {
  return Math.round(seconds / 60) < 1 ? 1 : Math.round(seconds / 60);
};

export function TimeLogsContent() {
  const { last7daysData } = useRoot();
  const [expanded, setExpanded] = useState(false);
  const [timeLogsData, setTimeLogsData] = useState({
    summary: {
      totalMinutes: 0,
      completed: 0,
      homeworkDone: 0,
      overdue: 0,
      upcoming: 0,
    },
    logs: [],
  });
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { LatexRenderView } = pkg;

  useEffect(() => {
    const fetchTimeLogs = async () => {
      try {
        const logs = await timeLogsDB.getAllTimeLogs();
        if (logs && logs.length > 0) {
          // Transform the logs data
          console.log(logs, " LOGS");
          const transformedLogs = logs.flatMap((journey) =>
            journey.timeLogs.map((timeLog) => ({
              date: formatDate(timeLog?.updatedAt),
              title:
                journey?.taskType === "Experiential Learning"
                  ? journey?.taskAttributes?.activityInfo?.name
                  : isNotEmptyOrNull(journey?.taskAttributes?.subtopicName)
                  ? journey?.taskAttributes?.subtopicName
                  : journey?.taskAttributes?.topicName,
              subject: `${
                journey?.taskType === "Experiential Learning"
                  ? journey?.taskAttributes?.activityInfo?.subject
                  : journey?.taskAttributes?.subjectName
              }: ${getTaskType(journey?.taskType)}`,
              minutes: secondsToMinutes(timeLog?.sessionTime),
              updatedAt: timeLog?.updatedAt,
            }))
          );
          // Sort logs by date (most recent first)
          transformedLogs.sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
          );
          console.log(transformedLogs, "TRANSFORMED LOGS");
          // Calculate summary
          const totalMinutes = transformedLogs.reduce(
            (sum, log) => sum + log.minutes,
            0
          );
          const completed = logs.length;
          const homeworkDone = logs.filter(
            (log) => log.taskType === "practice"
          ).length;

          setTimeLogsData({
            summary: {
              totalMinutes,
              completed,
              homeworkDone,
              overdue: 0, // These values would need to be calculated based on your business logic
              upcoming: 0,
            },
            logs: transformedLogs,
          });
        }
      } catch (error) {
        console.error("Error fetching time logs:", error);
      }
    };

    fetchTimeLogs();
  }, []);

  const logsToDisplay = expanded
    ? timeLogsData.logs
    : timeLogsData.logs.slice(
        0,
        isMobile ? INITIAL_VISIBLE_MOBILE : INITIAL_VISIBLE
      );

  return (
    <Box {...timeLogsContainer}>
      <Text fontWeight="600" color={"#1D287A"} fontSize="18px" mb={"8px"}>
        My time log
      </Text>
      <Box mb={4} p={"12px"} backgroundColor={"#A299D41A"} borderRadius={"4px"}>
        <HStack display={"flex"} justifyContent={"space-between"}>
          <Text
            fontWeight="500"
            color={"#000000"}
            fontSize="14px"
            lineHeight={"24px"}
          >
            Last 7 days
          </Text>
          <Text
            fontWeight="600"
            color={"#000000"}
            fontSize="14px"
            lineHeight={"24px"}
          >
            {timeLogsData.summary.totalMinutes}{" "}
            <Text as="span" fontWeight="300" color={"#000000"} fontSize="14px">
              min
            </Text>
          </Text>
        </HStack>

        {(timeLogsData.summary.totalMinutes > 0 ||
          last7daysData?.completed > 0) && (
          <Text
            fontWeight="500"
            color={"#000000"}
            fontSize="12px"
            lineHeight={"16px"}
            mt="4px"
          >
            Well done! {/* Only show minutes text if totalMinutes > 0 */}
            {timeLogsData.summary.totalMinutes > 0 && (
              <Text
                as="span"
                fontWeight="400"
                color={"#000000"}
                fontSize="12px"
                lineHeight={"16px"}
              >
                You've clocked {timeLogsData.summary.totalMinutes} minutes.{" "}
              </Text>
            )}
            {/* Only show homework text if completed > 0 */}
            {last7daysData?.completed > 0 && (
              <Text
                as="span"
                fontWeight="400"
                color={"#000000"}
                fontSize="12px"
                lineHeight={"16px"}
              >
                You've completed {last7daysData.completed} homework!
              </Text>
            )}
          </Text>
        )}
      </Box>
      <Flex mb={4} gap={2}>
        <Box {...statBox}>
          <Text {...statLabel}>Homework done</Text>
          <Text {...statValue}>{last7daysData?.completed}</Text>
        </Box>
        <Box {...statBoxOverdue}>
          <Text {...statLabel}>Overdue</Text>
          <Text {...statValue}>{last7daysData?.missed_opportunity}</Text>
        </Box>
        <Box {...statBoxUpcoming}>
          <Text {...statLabel}>Upcoming</Text>
          <Text {...statValue}>{last7daysData?.top_priority}</Text>
        </Box>
      </Flex>
      {logsToDisplay.length > 0 && (
        <Text
          fontWeight="600"
          color={"#000000"}
          fontSize="16px"
          fontFamily={"Poppins"}
          lineHeight={"24px"}
        >
          Recent
        </Text>
      )}
      <VStack align="stretch" spacing={3} {...(expanded ? logsScrollable : {})}>
        {logsToDisplay.map((log, idx) => (
          <HStack key={idx} {...logItem}>
            <Box>
              <Text
                fontWeight="300"
                color={"#00000099"}
                fontSize="12px"
                fontFamily={"Poppins"}
                lineHeight={"24px"}
              >
                {log.date}
              </Text>
              <Text
                fontWeight="600"
                color={"#000000"}
                fontSize="16px"
                fontFamily={"Poppins"}
                lineHeight={"16px"}
              >
                <LatexRenderView math={log?.title} />
                {/* {log.title} */}
              </Text>
              <Text
                fontWeight="300"
                color={"#00000099"}
                fontSize="12px"
                fontFamily={"Poppins"}
                lineHeight={"24px"}
              >
                {log.subject}
              </Text>
            </Box>
            <Badge {...logTime}>{log.minutes} min</Badge>
          </HStack>
        ))}
      </VStack>
      {!expanded && timeLogsData.logs.length > INITIAL_VISIBLE && (
        <Button
          mt={4}
          colorScheme="#130159"
          variant="ghost"
          size="sm"
          display={"flex"}
          justifyContent={"flex-end"}
          width="100%"
          onClick={() => setExpanded(true)}
        >
          view more <ChevronDownIcon ml={1} />
        </Button>
      )}
      {expanded && (
        <Button
          mt={4}
          colorScheme="#130159"
          variant="ghost"
          size="sm"
          display={"flex"}
          justifyContent={"flex-end"}
          width="100%"
          onClick={() => setExpanded(false)}
        >
          view less <ChevronUpIcon ml={1} />
        </Button>
      )}
    </Box>
  );
}

// Modal wrapper for use in Syllabus
export default function TimeLogsModal({ isOpen, onClose }) {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent
        position="fixed"
        top={isMobile ? "70px" : "76px"}
        left="auto"
        right={"80px"}
        m={0}
        maxW={{ base: "calc(100vw - 100px)", md: "400px" }}
        bg="transparent"
        width="auto"
        boxShadow="none"
        borderRadius="lg"
      >
        <ModalBody p={0}>
          <TimeLogsContent />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
